import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { allModules } from '../../mocks/modules';
import { renderMarkdown } from '../../utils/markdownRenderer';
import tradeLicenseDocumentation from './data/tradeLicenseDocumentation';

interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  content?: string;
}

// Module-specific documentation mapping
const moduleDocumentationMap: Record<string, TreeNode[]> = {
  'trade-license': tradeLicenseDocumentation,
};

export const DocumentationPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['overview']));
  const [selectedNode, setSelectedNode] = useState<string>('overview');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const module = allModules.find(m => m.id === moduleId);

  useEffect(() => {
    if (!module) {
      navigate('/');
    }
  }, [module, navigate]);

  // Auto-expand parent nodes when selecting a child
  useEffect(() => {
    const expandParents = (nodes: TreeNode[], targetId: string, path: string[] = []): string[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.id];
        if (node.id === targetId) {
          setExpandedNodes(prev => {
            const newSet = new Set(prev);
            currentPath.slice(0, -1).forEach(id => newSet.add(id));
            return newSet;
          });
          return currentPath;
        }
        if (node.children) {
          const found = expandParents(node.children, targetId, currentPath);
          if (found) return found;
        }
      }
      return null;
    };
    expandParents(documentationTree, selectedNode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode]);

  // Documentation tree structure - similar to UPYOG GitBook style
  // Use module-specific documentation if available, otherwise use default
  const documentationTree: TreeNode[] = useMemo(() => {
    // Check if module-specific documentation exists
    if (moduleId && moduleDocumentationMap[moduleId]) {
      return moduleDocumentationMap[moduleId];
    }

    // Default documentation tree
    return [
    {
      id: 'overview',
      label: 'Overview',
      content: `# ${module?.name || 'Module'} Overview\n\n${module?.description || ''}\n\nThis module provides comprehensive functionality for managing ${module?.name || 'module'} operations within the Andhra Pradesh Municipal ERP system built on the UPAYOG framework.\n\n## Module Information\n\n- **Category**: ${module?.category || 'N/A'}\n- **Module ID**: ${moduleId}\n- **API Documentation**: Available at ${module?.apiDocs || '/docs'}\n- **User Documentation**: Available at ${module?.userDocs || '/docs'}\n\n## Key Capabilities\n\nThis module enables:\n\n- Efficient management of ${module?.name || 'module'} operations\n- Seamless integration with other UPAYOG modules\n- Real-time data synchronization\n- Comprehensive reporting and analytics`
    },
    {
      id: 'getting-started',
      label: 'Getting Started',
      children: [
        {
          id: 'introduction',
          label: 'Introduction',
          content: `# Introduction to ${module?.name || 'Module'}\n\n## What is ${module?.name || 'this module'}?\n\n${module?.description || ''}\n\n## Key Benefits\n\n- **Efficiency**: Streamlined processes reduce manual work\n- **Transparency**: Real-time tracking and visibility\n- **Compliance**: Built-in compliance with government regulations\n- **Integration**: Seamless integration with other modules\n\n## Use Cases\n\n- Use case 1: Description\n- Use case 2: Description\n- Use case 3: Description`
        },
        {
          id: 'installation',
          label: 'Installation',
          content: `# Installation Guide\n\n## Prerequisites\n\nBefore installing the ${module?.name || 'module'}, ensure you have:\n\n- Node.js 18+ installed\n- PostgreSQL 14+ database\n- Redis for caching\n- Docker (optional, for containerized deployment)\n\n## Setup Steps\n\n### Step 1: Clone the Repository\n\n\`\`\`bash\ngit clone https://github.com/upyog/${moduleId}.git\ncd ${moduleId}\n\`\`\`\n\n### Step 2: Install Dependencies\n\n\`\`\`bash\nnpm install\n\`\`\`\n\n### Step 3: Configure Environment Variables\n\nCreate a \`.env\` file with the following variables:\n\n\`\`\`env\nDB_HOST=localhost\nDB_PORT=5432\nDB_NAME=erp_db\nDB_USER=erp_user\nDB_PASSWORD=your_password\nREDIS_HOST=localhost\nREDIS_PORT=6379\n\`\`\`\n\n### Step 4: Run Database Migrations\n\n\`\`\`bash\nnpm run migrate\n\`\`\`\n\n### Step 5: Start the Service\n\n\`\`\`bash\nnpm start\n\`\`\``
        },
        {
          id: 'configuration',
          label: 'Configuration',
          content: `# Configuration\n\n## Environment Variables\n\nConfigure the module using the following environment variables:\n\n\`\`\`env\n# Database Configuration\nDB_HOST=localhost\nDB_PORT=5432\nDB_NAME=erp_db\nDB_USER=erp_user\nDB_PASSWORD=your_password\n\n# Redis Configuration\nREDIS_HOST=localhost\nREDIS_PORT=6379\n\n# Application Configuration\nAPP_PORT=3000\nAPP_ENV=development\nLOG_LEVEL=info\n\`\`\`\n\n## Module Settings\n\nConfigure the module settings through:\n\n1. **Admin Panel**: Navigate to Settings > Modules > ${module?.name || 'Module'}\n2. **Configuration Files**: Edit \`config/${moduleId}.json\`\n3. **Environment Variables**: Set module-specific environment variables\n\n## Feature Flags\n\nEnable or disable features using feature flags:\n\n- \`ENABLE_FEATURE_A=true\`\n- \`ENABLE_FEATURE_B=false\``
        },
        {
          id: 'quick-start',
          label: 'Quick Start',
          content: `# Quick Start Guide\n\nFollow these steps to get started with ${module?.name || 'the module'}:\n\n## Step 1: Access the Module\n\nNavigate to the module from the main dashboard or use the direct URL: \`/app/${moduleId}\`\n\n## Step 2: Configure Initial Settings\n\n1. Go to Settings > ${module?.name || 'Module'} Configuration\n2. Set up basic parameters\n3. Configure user permissions\n\n## Step 3: Create Your First Record\n\n1. Click on "Create New" button\n2. Fill in the required information\n3. Submit the form\n\n## Step 4: Explore Features\n\n- Browse existing records\n- Use search and filter options\n- Generate reports\n- Explore integrations`
        }
      ]
    },
    {
      id: 'user-guide',
      label: 'User Guide',
      children: [
        {
          id: 'features',
          label: 'Features',
          content: `# Features\n\n## Key Features\n\n### Feature 1: Core Functionality\n\nDescription of the primary feature and how it works.\n\n**Benefits**:\n- Benefit 1\n- Benefit 2\n- Benefit 3\n\n### Feature 2: Advanced Capabilities\n\nDescription of advanced features available in the module.\n\n**Use Cases**:\n- Use case 1\n- Use case 2\n\n### Feature 3: Integration Features\n\nHow this module integrates with other UPAYOG modules.\n\n## Advanced Features\n\n- **Advanced Feature 1**: Detailed description\n- **Advanced Feature 2**: Detailed description\n- **Advanced Feature 3**: Detailed description`
        },
        {
          id: 'workflows',
          label: 'Workflows',
          content: `# Workflows\n\n## Standard Workflow\n\n### Step 1: Initial Setup\n\nDescription of the first step in the workflow.\n\n### Step 2: Data Entry\n\nDescription of data entry process.\n\n### Step 3: Review and Approval\n\nDescription of review and approval process.\n\n### Step 4: Completion\n\nDescription of completion and finalization.\n\n## Alternative Workflows\n\n### Workflow A: Simplified Process\n\nFor simple cases, you can use this streamlined workflow.\n\n### Workflow B: Advanced Process\n\nFor complex scenarios, use this detailed workflow.\n\n## Workflow Diagrams\n\n[Workflow diagrams would be displayed here]`
        },
        {
          id: 'best-practices',
          label: 'Best Practices',
          content: `# Best Practices\n\n## Recommended Practices\n\n### Practice 1: Data Management\n\n- Always validate data before submission\n- Use consistent naming conventions\n- Keep records up to date\n\n### Practice 2: Security\n\n- Use strong passwords\n- Enable two-factor authentication\n- Regularly review access permissions\n\n### Practice 3: Performance\n\n- Optimize queries for large datasets\n- Use caching where appropriate\n- Monitor system performance\n\n## Common Pitfalls\n\n### Pitfall 1: Data Inconsistency\n\n**Problem**: Description of the problem\n\n**Solution**: How to avoid or fix this issue\n\n### Pitfall 2: Performance Issues\n\n**Problem**: Description of the problem\n\n**Solution**: How to avoid or fix this issue`
        },
        {
          id: 'screenshots',
          label: 'Screenshots & UI Details',
          content: `# Screenshots & UI Details\n\n## Main Dashboard\n\n[Main dashboard screenshot and description]\n\n## Create Form\n\n[Create form screenshot and description]\n\n## Search & Filter\n\n[Search and filter interface screenshot]\n\n## Reports\n\n[Reports interface screenshot]`
        }
      ]
    },
    {
      id: 'api-reference',
      label: 'API Reference',
      children: [
        {
          id: 'authentication',
          label: 'Authentication',
          content: `# API Authentication\n\nAll API requests require authentication using Bearer tokens.\n\n## Obtaining a Token\n\n1. Login to the system\n2. Navigate to API Settings\n3. Generate an API token\n\n## Using the Token\n\nInclude the token in the Authorization header:\n\n\`\`\`javascript\nheaders: {\n  'Authorization': 'Bearer YOUR_TOKEN',\n  'Content-Type': 'application/json'\n}\n\`\`\`\n\n## Token Expiration\n\nTokens expire after 24 hours. Refresh tokens are available for extended sessions.`
        },
        {
          id: 'endpoints',
          label: 'Endpoints',
          content: `# API Endpoints\n\n## Base URL\n\n\`https://api.ap-ulb-erp.gov.in/v1\`\n\n## Available Endpoints\n\n### List Records\n\n\`GET /api/${moduleId}\`\n\nReturns a list of all records.\n\n**Query Parameters**:\n- \`page\`: Page number (default: 1)\n- \`limit\`: Records per page (default: 10)\n- \`sort\`: Sort field\n- \`order\`: Sort order (asc/desc)\n\n### Create Record\n\n\`POST /api/${moduleId}\`\n\nCreates a new record.\n\n**Request Body**:\n\`\`\`json\n{\n  "field1": "value1",\n  "field2": "value2"\n}\n\`\`\`\n\n### Get Record by ID\n\n\`GET /api/${moduleId}/:id\`\n\nReturns a specific record by ID.\n\n### Update Record\n\n\`PUT /api/${moduleId}/:id\`\n\nUpdates an existing record.\n\n### Delete Record\n\n\`DELETE /api/${moduleId}/:id\`\n\nDeletes a record.`
        },
        {
          id: 'examples',
          label: 'Code Examples',
          content: `# Code Examples\n\n## JavaScript/Node.js\n\n### Fetch Records\n\n\`\`\`javascript\n// Example API call\nfetch('/api/${moduleId}', {\n  method: 'GET',\n  headers: {\n    'Authorization': 'Bearer TOKEN',\n    'Content-Type': 'application/json'\n  }\n})\n.then(response => response.json())\n.then(data => console.log(data))\n.catch(error => console.error('Error:', error));\n\`\`\`\n\n### Create Record\n\n\`\`\`javascript\nfetch('/api/${moduleId}', {\n  method: 'POST',\n  headers: {\n    'Authorization': 'Bearer TOKEN',\n    'Content-Type': 'application/json'\n  },\n  body: JSON.stringify({\n    field1: 'value1',\n    field2: 'value2'\n  })\n})\n.then(response => response.json())\n.then(data => console.log('Created:', data));\n\`\`\`\n\n## Python\n\n\`\`\`python\nimport requests\n\n# Get records\nresponse = requests.get(\n    '/api/${moduleId}',\n    headers={'Authorization': 'Bearer TOKEN'}\n)\ndata = response.json()\n\n# Create record\nresponse = requests.post(\n    '/api/${moduleId}',\n    headers={'Authorization': 'Bearer TOKEN'},\n    json={'field1': 'value1', 'field2': 'value2'}\n)\n\`\`\``
        },
        {
          id: 'error-handling',
          label: 'Error Handling',
          content: `# Error Handling\n\n## HTTP Status Codes\n\n- \`200 OK\`: Request successful\n- \`201 Created\`: Resource created successfully\n- \`400 Bad Request\`: Invalid request parameters\n- \`401 Unauthorized\`: Authentication required\n- \`403 Forbidden\`: Insufficient permissions\n- \`404 Not Found\`: Resource not found\n- \`500 Internal Server Error\`: Server error\n\n## Error Response Format\n\n\`\`\`json\n{\n  "error": {\n    "code": "ERROR_CODE",\n    "message": "Error message",\n    "details": {}\n  }\n}\n\`\`\``
        }
      ]
    },
    {
      id: 'troubleshooting',
      label: 'Troubleshooting',
      children: [
        {
          id: 'common-issues',
          label: 'Common Issues',
          content: `# Common Issues\n\n## Issue 1: Connection Problems\n\n**Problem**: Unable to connect to the database\n\n**Solution**: \n1. Check database credentials\n2. Verify network connectivity\n3. Ensure database service is running\n\n## Issue 2: Authentication Errors\n\n**Problem**: Getting 401 Unauthorized errors\n\n**Solution**:\n1. Verify API token is valid\n2. Check token expiration\n3. Regenerate token if needed\n\n## Issue 3: Performance Issues\n\n**Problem**: Slow response times\n\n**Solution**:\n1. Check database indexes\n2. Optimize queries\n3. Enable caching\n4. Review server resources`
        },
        {
          id: 'faq',
          label: 'FAQ',
          content: `# Frequently Asked Questions\n\n## Q: How do I reset my password?\n\nA: Navigate to Profile > Security > Change Password and follow the instructions.\n\n## Q: Can I export data from this module?\n\nA: Yes, you can export data in various formats (CSV, Excel, PDF) from the Reports section.\n\n## Q: How do I integrate this module with other systems?\n\nA: Use the API endpoints documented in the API Reference section. All endpoints support standard REST protocols.\n\n## Q: Is there a mobile app for this module?\n\nA: Yes, mobile apps are available for iOS and Android. Check the App Store section for download links.\n\n## Q: How do I report a bug?\n\nA: Use the Support section to submit a ticket or contact the technical support team.`
        }
      ]
    },
    {
      id: 'release-notes',
      label: 'Release Notes',
      content: `# Release Notes\n\n## Version 2.0\n\n### New Features\n\n- Feature 1: Description\n- Feature 2: Description\n- Feature 3: Description\n\n### Enhancements\n\n- Enhancement 1: Description\n- Enhancement 2: Description\n\n### Bug Fixes\n\n- Fixed issue with data synchronization\n- Resolved performance problems\n- Corrected UI display issues\n\n## Version 1.5\n\n### Previous Updates\n\n- Previous feature additions\n- Previous enhancements`
    }
  ];
  }, [module, moduleId]);

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const findNodeContent = (nodes: TreeNode[], id: string): string | undefined => {
    for (const node of nodes) {
      if (node.id === id) {
        return node.content;
      }
      if (node.children) {
        const found = findNodeContent(node.children, id);
        if (found) return found;
      }
    }
    return undefined;
  };

  // Filter tree nodes based on search term
  const filterTreeNodes = (nodes: TreeNode[], search: string): TreeNode[] => {
    if (!search.trim()) return nodes;
    
    const filtered: TreeNode[] = [];
    const searchLower = search.toLowerCase();
    
    nodes.forEach(node => {
      const matchesLabel = node.label.toLowerCase().includes(searchLower);
      const matchingChildren = node.children ? filterTreeNodes(node.children, search) : [];
      
      if (matchesLabel || matchingChildren.length > 0) {
        filtered.push({
          ...node,
          children: matchingChildren.length > 0 ? matchingChildren : node.children
        });
      }
    });
    
    return filtered;
  };

  const filteredTree = useMemo(() => filterTreeNodes(documentationTree, searchTerm), [searchTerm, documentationTree]);

  const renderTreeNode = (node: TreeNode, level: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode === node.id;

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center py-2 px-2 rounded-md cursor-pointer transition-all duration-200 ${
            isSelected
              ? 'bg-amber-100 text-amber-700 font-semibold border-l-2 border-amber-600'
              : 'text-stone-700 hover:bg-stone-100 hover:text-stone-900'
          }`}
          style={{ paddingLeft: `${level * 1.25 + 0.5}rem` }}
          onClick={() => {
            if (hasChildren) {
              toggleNode(node.id);
            } else {
              setSelectedNode(node.id);
            }
          }}
        >
          {hasChildren ? (
            <i
              className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line w-4 h-4 flex items-center justify-center mr-2 transition-transform text-stone-500`}
            ></i>
          ) : (
            <i className="ri-file-line w-4 h-4 flex items-center justify-center mr-2 text-stone-400"></i>
          )}
          <span className="text-sm font-medium">{node.label}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="ml-2 border-l border-stone-200">
            {node.children!.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const currentContent = findNodeContent(documentationTree, selectedNode) || '';

  if (!module) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-1 flex pt-56">
        {/* Left Sidebar - Tree Navigation (GitBook style) */}
        <aside className="w-72 bg-white border-r border-stone-200 overflow-y-auto sticky top-48 h-[calc(100vh-12rem)] shadow-sm">
          <div className="p-6 pt-8">
            <div className="mb-6 pb-6 border-b border-stone-200">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className={`${module.icon} w-5 h-5 flex items-center justify-center text-amber-600`}></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-stone-900">{module.name}</h2>
                  <p className="text-xs text-stone-500 mt-0.5">{module.category}</p>
                </div>
              </div>
              <p className="text-xs text-stone-600 leading-relaxed">{module.description}</p>
            </div>
            
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <i className="ri-search-line w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400"></i>
                <input
                  type="text"
                  placeholder="Search documentation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                )}
              </div>
            </div>
            
            <nav className="space-y-1">
              {filteredTree.map(node => renderTreeNode(node))}
              {filteredTree.length === 0 && searchTerm && (
                <div className="text-center py-8 text-stone-500 text-sm">
                  <i className="ri-search-line w-8 h-8 flex items-center justify-center mx-auto mb-2 text-stone-400"></i>
                  <p>No results found for "{searchTerm}"</p>
                </div>
              )}
            </nav>
          </div>
        </aside>

        {/* Right Content Area */}
        <main className="flex-1 overflow-y-auto bg-stone-50">
          <div className="max-w-4xl mx-auto px-8 py-12">
            <article className="bg-white rounded-lg shadow-sm border border-stone-200 p-8 md:p-12">
              <div className="prose prose-stone prose-lg max-w-none">
                {renderMarkdown(currentContent)}
              </div>
            </article>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

